import { Input, Text, Stack, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';
import { useRecoilValue } from 'recoil';

import { customerDetailsEmail } from '../../../../../../../../store/customerDetail/privatePension/atom';

import {
  getValidationCreateSchema,
  PrivatePensionSecondCopyData,
  PrivatePensionSecondCopyFormData,
} from './constants';

export interface SecondCopyEmailFieldsProps {
  setButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  emails: PrivatePensionSecondCopyData;
  setEmails: React.Dispatch<React.SetStateAction<PrivatePensionSecondCopyData>>;
}

export const SecondCopyEmailFields: React.FC<SecondCopyEmailFieldsProps> = ({
  emails,
  setEmails,
  setButtonDisabled,
}) => {
  const direction = useBreakpointValue({ base: 'column', sm: 'row' });

  const clientEmail = useRecoilValue(customerDetailsEmail);

  const {
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<PrivatePensionSecondCopyFormData>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      clientEmail: clientEmail ?? '',
      advisorEmail: '',
    },
    resolver: zodResolver(getValidationCreateSchema()),
  });

  const handleClientEmailChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setEmails((state) => ({ ...state, clientEmail: e.nativeEvent.text }));
    },
    [setEmails]
  );

  const handleAdvisorEmailChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setEmails((state) => ({ ...state, advisorEmail: e.nativeEvent.text }));
    },
    [setEmails]
  );

  useEffect(() => {
    if (clientEmail) {
      setValue('clientEmail', clientEmail, { shouldValidate: true });
      setEmails((state) => ({ ...state, clientEmail }));
    }
  }, [clientEmail, setValue, setEmails]);

  useEffect(() => {
    setButtonDisabled(!isValid);
  }, [setButtonDisabled, isValid]);

  return (
    <Stack direction={direction} justifyContent="space-between" space={Spacing['stack.xxxs']}>
      <VStack space={Spacing['stack.quarck']} flex={1}>
        <Text>E-mail do cliente</Text>
        <Input
          placeholder="Digite o email do cliente"
          control={control}
          name="clientEmail"
          autoComplete="off"
          value={emails.clientEmail}
          onChange={handleClientEmailChange}
          variant="filled"
          hasShadow={false}
          error={errors.clientEmail}
        />
      </VStack>
      <VStack space={Spacing['stack.quarck']} flex={1}>
        <Text>E-mail cópia</Text>
        <Input
          placeholder="Digite o email do assessor"
          control={control}
          name="advisorEmail"
          autoComplete="off"
          value={emails.advisorEmail}
          onChange={handleAdvisorEmailChange}
          variant="filled"
          hasShadow={false}
          error={errors.advisorEmail}
        />
      </VStack>
    </Stack>
  );
};
